import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import CommentAiService, { IGenerateCommentOptions } from '../../services/CommentAiService';
import { useTranslation } from 'react-i18next';
import { COMMENT_GENERATION_LANGUAGES } from '../../constants/commentGenerationLanguages';

const AICommentGenerator = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [language, setLanguage] = useState<Language | ''>('');
  const [tonality, setTonality] = useState<Tonality | ''>('');
  const [review, setReview] = useState('');
  const [result, setResult] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [isCopyDisabled, setIsCopyDisabled] = useState(true);
  const [personalReference, setPersonalReference] = useState(false);
  const [salutation, setSalutation] = useState('');
  const [greeting, setGreeting] = useState('');
  const [additionalPrompt, setAdditionalPrompt] = useState('');

  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setResult('');

    const generateCommentOptions: IGenerateCommentOptions = {
      language,
      tonality,
      duzen: personalReference,
      review,
      useCustomerName: false,
      salutation: salutation,
      signature: greeting,
      additionalPrompt: additionalPrompt,
      reviewMetadata: {
        score: 5,
        author: '',
        platform: '',
      },
    };

    const data = await CommentAiService.generateComment(generateCommentOptions, setLoading);

    if (!data) return;

    setResult(data);
  };

  useEffect(() => {
    if (language === '' || tonality === '' || review === '' || loading) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }

    if (result === '') {
      setIsCopyDisabled(true);
    } else {
      setIsCopyDisabled(false);
    }
  }, [language, tonality, review, result, loading]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex' }}>
          <FormControl
            size="small"
            fullWidth
            sx={{ minWidth: 300, maxWidth: 300 }}
          >
            <InputLabel id="language-label">{t('language')}</InputLabel>
            <Select
              labelId="language-label"
              label="Language"
              value={language}
              onChange={(e: SelectChangeEvent) => {
                setLanguage(e.target.value as Language);
              }}
            >
              <MenuItem value="">
                <em>---</em>
              </MenuItem>
              {COMMENT_GENERATION_LANGUAGES.map(({ value, labelKey }) => (
                <MenuItem
                  key={value}
                  value={value}
                >
                  {t(labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            fullWidth
            sx={{ ml: theme.spacing(2), minWidth: 300, maxWidth: 300 }}
          >
            <InputLabel id="tonalitiy-label">{t('tonality')}</InputLabel>
            <Select
              labelId="tonality-label"
              label={t('tonality')}
              value={tonality}
              onChange={(e: SelectChangeEvent) => {
                setTonality(e.target.value as Tonality);
                setPersonalReference(false);
              }}
            >
              <MenuItem value="">
                <em>---</em>
              </MenuItem>
              <MenuItem value={'motivating'}>{t('tonality_motivating')}</MenuItem>
              <MenuItem value={'constructive'}>{t('tonality_constructive')}</MenuItem>
              <MenuItem value={'formal'}>{t('tonality_formal')}</MenuItem>
              <MenuItem value={'personal'}>{t('tonality_personal')}</MenuItem>
              <MenuItem value={'informal'}>{t('tonality_informal')}</MenuItem>
            </Select>
          </FormControl>
          {language && language !== 'en' && (
            <FormControlLabel
              control={
                <Switch
                  checked={personalReference}
                  onChange={(event: ChangeEvent<HTMLInputElement>, checked) =>
                    setPersonalReference(checked)
                  }
                />
              }
              label="Duzen"
              sx={{ ml: theme.spacing(2) }}
            />
          )}
        </Box>
        <TextField
          label={t('salutation')}
          size="small"
          onChange={(e) => setSalutation(e.target.value)}
          sx={{ mt: theme.spacing(2), minWidth: 300, maxWidth: 300 }}
        />
        <TextField
          label={t('greeting')}
          size="small"
          multiline
          rows={2}
          onChange={(e) => setGreeting(e.target.value)}
          sx={{ mt: theme.spacing(2), minWidth: 300, maxWidth: 300 }}
        />
        <TextField
          label={t('additional_prompt')}
          size="small"
          multiline
          rows={2}
          onChange={(e) => setAdditionalPrompt(e.target.value)}
          sx={{ mt: theme.spacing(2), minWidth: 300, maxWidth: 300 }}
        />
        <TextField
          label={t('review_text')}
          multiline
          rows={4}
          onChange={(e) => setReview(e.target.value as string)}
          sx={{ mt: theme.spacing(2) }}
        />
        <Box sx={{ mt: theme.spacing(2), minWidth: 300 }}>
          <Button
            variant="contained"
            onClick={async () => await handleSubmit()}
            disabled={isDisabled}
          >
            {t('generate')}
          </Button>
          <Button
            variant="contained"
            disabled={isCopyDisabled}
            onClick={async () => {
              await navigator.clipboard.writeText(result);
            }}
            sx={{ ml: theme.spacing(1) }}
          >
            {t('copy_result')}
          </Button>
        </Box>
        <Typography variant="caption">{t('ai_disclaimer_reply')}</Typography>
      </Box>
      <Box sx={{ display: 'flex', mt: theme.spacing(2) }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: theme.spacing(2),
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {result && (
              <>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {t('result')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: theme.spacing(2), whiteSpace: 'pre-line' }}
                >
                  {result}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AICommentGenerator;
